import { Button, IconButton } from '@mui/material';
import { ArrowBackOutlined, ArrowForwardOutlined } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import type { CSSProperties, FC } from 'react';
import { useThemeToggle } from '../ThemeProvider/WorkingThemeProvider';
import Header from '../Header/Header';
import Footer from '../Footer/Footer';
import styles from './HomePage.module.scss';

/**
 * How a slide is framed inside the hero — the equivalent of an SVG viewBox.
 *
 * The frame is the slide's box minus `inset`. The media is sized and placed so
 * that its `content` box (not the whole file, which may carry white margins or
 * artefacts) fits the frame and shares its centre. Both are computed from
 * container-query units, so the result holds at any viewport size or aspect.
 *
 *  fit      'contain' fits the whole content box inside the frame;
 *           'cover' fills the frame and crops the overflow.
 *  inset    space kept clear around the media (CSS inset shorthand, 1-4 values).
 *           `var(--caption-h)` is the caption bar's height, so a slide can be
 *           kept clear of it.
 *  aspect   width / height of the media file itself.
 *  content  the part of the media to actually frame, in fractions of the file
 *           (x, y, w, h). Defaults to all of it. Use it to trim dead margins
 *           or a baked-in artefact so the *visible* content is what gets
 *           centred and scaled.
 *  scale    zoom factor applied after fitting; 1 = no zoom.
 *  offsetY  optional final nudge, e.g. '-2%' of the media height.
 */
interface SlideFrame {
  fit: 'contain' | 'cover';
  inset?: string;
  aspect: number;
  content?: { x: number; y: number; w: number; h: number };
  scale?: number;
  offsetY?: string;
}

interface CarouselItem {
  id: number;
  type: 'image' | 'video' | 'vimeo';
  src: string;
  /** Variant re-grounded on the dark surface, shown when the page is dark. */
  srcDark?: string;
  frame: SlideFrame;
  eyebrow: string;
  title: string;
  description: string;
}

const carouselItems: CarouselItem[] = [
  {
    id: 1,
    type: 'image',
    src: '/slideshow/dbbs-pipeline.png',
    srcDark: '/slideshow/dbbs-pipeline-dark.webp',
    // Panelled figure: show all of it, clear of the tags and the caption bar.
    frame: {
      fit: 'contain',
      inset: 'var(--space-16) var(--space-12) calc(var(--caption-h) + var(--space-12))',
      aspect: 3000 / 2000,
      content: { x: 0.016, y: 0.017, w: 0.974, h: 0.966 },
    },
    eyebrow: 'Multi-scale reconstruction',
    title: 'A multi-scale approach to investigate the mouse cerebellum',
    description: `Our group is tackling the modeling of the mouse cerebellum using multiple approaches. Each panel of this figure represents one of these approaches, sorted by the scale of the circuits reconstructed.

A. Multi-Electrode-Array recording of Purkinje cells (PC) in vitro: The top image shows the parasagittal slice of the cerebellum recorded and the detected PC layer activity in levels of red. The bottom trace shows the electrophysiological recording of PC spontaneous activity. (Mapelli et al. 2025)

B. PC morphology is reconstructed in 3D and its electrical parameters are extracted from experimental recordings. The resulting model can be simulated with great details. (Masoli and D'Angelo 2017)

C. Microcircuit of the cerebellar cortex built based on the cell models previously described. Each cell is placed and connected to the rest of the circuit to form a network in which neurons can be simulated as compartmental or point-neurons. (De Schepper et al. 2022)

D. The microcircuit of the cerebellar cortex can be extended to a whole region (here the Declive) by orienting, scaling and bending the cells to fit into this space. (Rodarie et al. in preparation)

E. Cell populations can be simplified into mean field models which represent the activity of the whole population allowing simulations of large scale networks. With this technique, we can model and simulate the whole cerebellar cortex. (Lorenzi et al. 2023)

F. The cerebellum circuit is here integrated into a whole mouse brain model. In this network each node represents a brain region's activity and edges correspond to synaptic connections observed between these regions. (De Grazia et al. in preparation)`,
  },
  {
    id: 2,
    type: 'video',
    src: '/slideshow/microcircuit_net_long.mp4',
    // Immersive: fills the hero edge to edge, overflow cropped.
    frame: { fit: 'cover', aspect: 1280 / 750 },
    eyebrow: 'Morphology',
    title: '3D morphology-based reconstruction of the cerebellar cortical circuit',
    description:
      'A subset of a cerebellar cortex circuit is displayed here. Each cell is displayed in 3D (morphology) and in color, the granule cells in red with their ascending axons and long parallel fibers; in blue the Golgi cells; in green the Purkinje cells; and in yellow and orange the Stellate and Basket cells respectively.',
  },
  {
    id: 3,
    type: 'video',
    src: '/slideshow/neural_act_drawn.mp4',
    srcDark: '/slideshow/neural_act_drawn-dark.mp4',
    // The clip is cropped to the figure itself, so the whole frame is content.
    frame: {
      fit: 'contain',
      inset: 'var(--space-16) var(--space-12) calc(var(--caption-h) + var(--space-12))',
      aspect: 1204 / 832,
    },
    eyebrow: 'Whole brain',
    title: 'Cerebellar nodes in the human whole-brain model',
    description:
      'Our group is tackling the modeling of human cerebellum embedding the cerebellar-specific dynamics in the Virtual Brain framework. Here the time series of activity of some cerebellar nodes are displayed, with intra-cerebellar differentiation in terms of structural and functional properties.',
  },
];

// Figures describing the lab's modelling work rather than this site's dataset.
// 6:       the group's own multi-scale figure, panels A-F (in vitro recording,
//          single cell, microcircuit, atlas region, mean field, whole brain)
// 29,230:  De Schepper et al. 2022, Commun Biol - multi-compartmental neurons
//          (plus 2,453 mossy fibre and glomerulus elements)
// 96,737:  Casali et al. 2019, Front Neuroinform - point-neuron scaffold model
// 33:      Lorenzi et al. 2025, npj Syst Biol Appl - the cerebellar part of the
//          whole-brain parcellation: 27 cerebellar cortical + 6 deep cerebellar
//          nuclei (the other 93 regions of the 126 are cerebral)
const figures: Array<[string, string]> = [
  ['6', 'Multiscale models'],
  ['29,230', 'Multicompartmental cells'],
  ['96,737', 'Spiking neurons'],
  ['33', 'Cerebellar nodes'],
];

const SLIDE_INTERVAL_MS = 7000;

/** Frame geometry as CSS custom properties; the arithmetic lives in the SCSS. */
const frameVars = (f: SlideFrame): CSSProperties => {
  const c = f.content ?? { x: 0, y: 0, w: 1, h: 1 };
  return {
    '--slide-inset': f.inset ?? '0px',
    '--slide-aspect': f.aspect,
    '--content-w': c.w,
    '--content-h': c.h,
    // Centre of the content box, as a fraction of the media.
    '--content-cx': c.x + c.w / 2,
    '--content-cy': c.y + c.h / 2,
    '--slide-scale': f.scale ?? 1,
    '--slide-offset-y': f.offsetY ?? '0px',
  } as CSSProperties;
};

const pad = (n: number) => String(n).padStart(2, '0');

const HomePage: FC = () => {
  const navigate = useNavigate();
  const { themeMode } = useThemeToggle();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  const [captionOpen, setCaptionOpen] = useState(false);
  // Slide media is costly to start and to fetch, so nothing loads until its
  // slide has been reached, and then it stays mounted for later passes.
  const [seen, setSeen] = useState<number[]>([0]);
  const captionBody = useRef<HTMLParagraphElement>(null);

  const count = carouselItems.length;
  const current = carouselItems[currentSlide];

  const goTo = (index: number) => {
    setCurrentSlide((index + count) % count);
    setCaptionOpen(false);
  };

  useEffect(() => {
    setSeen((prev) => (prev.includes(currentSlide) ? prev : [...prev, currentSlide]));
  }, [currentSlide]);

  // A collapsed caption shows its first lines, so the scroll box cannot be
  // left part-way down from the last time it was open.
  useEffect(() => {
    if (!captionOpen && captionBody.current) {
      captionBody.current.scrollTop = 0;
    }
  }, [captionOpen, currentSlide]);

  // Auto-advance pauses while the reader is over the hero or reading a caption.
  useEffect(() => {
    if (isHovered || captionOpen) return;
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % count);
    }, SLIDE_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [isHovered, captionOpen, count]);

  const handleNavItemClick = (item: string) => {
    switch (item) {
      case 'Model Exploration':
        navigate('/model-exploration');
        break;
      case 'Tools':
        navigate('/tools');
        break;
      case 'Learning':
        navigate('/learning');
        break;
      case 'Contact':
        navigate('/contact');
        break;
      default:
        break;
    }
  };

  return (
    <div className={styles.page}>
      <Header onNavItemClick={handleNavItemClick} />

      <main>
        <section
          className={styles.hero}
          aria-roledescription="carousel"
          aria-label="Featured research"
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          <div className={styles.hero__media}>
            {carouselItems.map((item, index) => {
              const active = index === currentSlide;
              return (
                <div
                  key={item.id}
                  className={styles.hero__slide}
                  data-active={active}
                  data-fit={item.frame.fit}
                  aria-hidden={!active}
                  style={frameVars(item.frame)}
                >
                  <div className={styles.hero__frame}>
                    {item.type === 'image' && (
                      <img
                        src={themeMode === 'dark' && item.srcDark ? item.srcDark : item.src}
                        alt={item.title}
                      />
                    )}
                    {item.type === 'vimeo' && seen.includes(index) && (
                      <iframe
                        src={item.src}
                        title={item.title}
                        allow="autoplay; fullscreen; picture-in-picture"
                        allowFullScreen
                      />
                    )}
                    {item.type === 'video' && seen.includes(index) && (
                      <video
                        src={themeMode === 'dark' && item.srcDark ? item.srcDark : item.src}
                        autoPlay
                        muted
                        loop
                        playsInline
                      />
                    )}
                  </div>
                </div>
              );
            })}

            <div className={styles.hero__tags}>
              <span className={styles.hero__tag}>{current.eyebrow}</span>
              <span className={`${styles.hero__tag} ${styles['hero__tag--data']}`}>
                {pad(currentSlide + 1)} / {pad(count)}
              </span>
            </div>
          </div>

          <div className={styles.caption} data-open={captionOpen}>
            <div className={styles.caption__inner}>
              <div className={styles.caption__text}>
                <h1 className={styles.caption__title}>{current.title}</h1>
                <p ref={captionBody} className={styles.caption__body}>
                  {current.description}
                </p>
                {current.description.length > 240 && (
                  <button
                    type="button"
                    className={styles.caption__toggle}
                    aria-expanded={captionOpen}
                    onClick={() => setCaptionOpen((open) => !open)}
                  >
                    {captionOpen ? 'Collapse caption' : 'Read full caption'}
                  </button>
                )}
              </div>

              <div className={styles.caption__controls}>
                <div className={styles.caption__dots}>
                  {carouselItems.map((item, index) => (
                    <button
                      key={item.id}
                      type="button"
                      className={styles.caption__dot}
                      data-active={index === currentSlide}
                      aria-label={`Slide ${index + 1}`}
                      aria-current={index === currentSlide}
                      onClick={() => goTo(index)}
                    />
                  ))}
                </div>
                <IconButton
                  className={styles.caption__arrow}
                  aria-label="Previous slide"
                  onClick={() => goTo(currentSlide - 1)}
                >
                  <ArrowBackOutlined fontSize="small" />
                </IconButton>
                <IconButton
                  className={styles.caption__arrow}
                  aria-label="Next slide"
                  onClick={() => goTo(currentSlide + 1)}
                >
                  <ArrowForwardOutlined fontSize="small" />
                </IconButton>
              </div>
            </div>
          </div>
        </section>

        {/* Readout strip: what the scene above actually contains. It carries
            the hero into the page instead of a rule sitting between them. */}
        <section className={styles.readout}>
          <dl className={styles.readout__inner}>
            {figures.map(([value, label]) => (
              <div key={label} className={styles.readout__item}>
                <dt className={styles.readout__label}>{label}</dt>
                <dd className={styles.readout__value}>{value}</dd>
              </div>
            ))}
          </dl>
        </section>

        <section className={styles.intro}>
          <span className={styles.intro__eyebrow}>The Cerebellum Facility Hub</span>
          <p className={styles.intro__statement}>
            Reconstructions, simulations and tools for the cerebellum — from a single Purkinje
            cell to a whole-brain network.
          </p>
          <div className={styles.intro__actions}>
            <Button variant="contained" size="large" onClick={() => navigate('/model-exploration')}>
              Explore the models
            </Button>
            <Button variant="outlined" size="large" onClick={() => navigate('/tools')}>
              Browse the tools
            </Button>
          </div>
        </section>

      </main>

      <Footer />
    </div>
  );
};

export default HomePage;
